export type NavItem = {
  /** The label, as shown in the nav. */
  label: string;
  /** The link to the page used in the nav. Often used if the nav redirects to another existing page. If not provided, the path will be used. */
  href?: string;
  /** The path to the mdx page. */
  path?: string;
  /** The sub-items in the nav. */
  nav?: NavItem[];
  /**
   * Private nav items are only shown to logged in users, and are excluded from search.
   *
   * IMPORTANT: When adding a private page, update all three places:
   *    1. Add it to `privatePages` in `src/config/docs-manifest.json` (the source of truth).
   *    2. Add it to `noIndexPages` in both `src/config/docs-manifest.json` and
   *       `src/util/no-index-pages.js` to mark it noIndex and exclude it from the sitemap.
   *       Without this, the page is hidden from nav/search but still crawlable by search engines.
   *    3. Set `private: true` here in the nav config.
   *       Exception: destinations and sources auto-populated from Sanity don't
   *       appear in this file as leaf items. For those, step 1 (adding the
   *       page to `privatePages` in the manifest) is enough — `nav.tsx` looks
   *       up merged Sanity items against the manifest at render time and hides
   *       them from users without the `docs-show-private-pages` flag.
   * Do NOT add it to `excludedSearchPages` — private pages are indexed in Algolia with an
   * `isPrivate` flag and filtered client-side based on auth state instead.
   * Run `node scripts/validate-config-sync.mjs` to verify all files stay in sync.
   */
  private?: boolean;
  /** When true, a visual divider is rendered after this item in the nav list. */
  dividerAfter?: boolean;
};

/** A group of nav items displayed under an optional heading in the sidebar. */
export type NavGroup = {
  /** The heading displayed above this group of items. Omit for standalone items that need no header. */
  heading?: string;
  /** The nav items in this group. */
  items: NavItem[];
};

/**
 * ! IMPORTANT !
 * When adding a new item to the root level of the config, you must also include it in:
 * `roots` in `scripts/update-algolia-index.mjs`
 * to ensure it is indexed by algolia.
 *
 * When marking a page as private, see the `private` field docs on `NavItem` above for
 * the full checklist — and do NOT add it to `excludedSearchPages`, which is deprecated.
 *
 * TODO: consolidate this split logic so it's not as convoluted. We can't directly import the config to update-algolia-index.mjs,
 * so we need to duplicate the logic here. Consider writing a script that could be used in update-algolia-index.mjs.
 */
/** Flat list of all nav items — used by Algolia indexing and other consumers that don't need grouping. */
export const config: NavItem[] = [
  {
    label: "Get started",
    path: "/getting-started",
    nav: [
      {
        label: "What is Hightouch?",
        path: "/welcome",
        href: "/",
      },
      {
        label: "How data activation works",
        path: "/concepts",
      },
      {
        label: "Start here",
        nav: [
          {
            label: "Platform admins",
            path: "/it",
          },
          {
            label: "Data teams",
            path: "/data",
          },
          {
            label: "Marketers",
            path: "/business",
          },
        ],
      },
    ],
  },
  {
    label: "Syncs",
    path: "/syncs",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Set up a sync",
        nav: [
          {
            label: "Create your first sync",
            path: "/create-your-first-sync",
          },
          {
            label: "Choose a sync type and mode",
            path: "/types-and-modes",
          },
          {
            label: "Match records to destination records",
            path: "/record-matching",
          },
          {
            label: "Map model columns to destination fields",
            path: "/mapping-data",
          },
          {
            label: "Array expansion",
            path: "/array-expansion",
          },
          {
            label: "Sync array identifiers to audiences",
            path: "/array-identifier-fan-out",
          },
          {
            label: "Data types and casting",
            href: "/models/data-types-casting",
          },
          {
            label: "Data extraction",
            path: "/data-extraction",
          },
        ],
      },
      {
        label: "Run and schedule syncs",
        nav: [
          {
            label: "Schedule a sync in the UI",
            path: "/schedule-sync-ui",
          },
          {
            label: "Run syncs in a sequence",
            path: "/schedule-sync-with-sequences",
          },
          {
            label: "Trigger a sync with dbt Cloud",
            href: "/extensions/dbt-cloud",
          },
          {
            label: "Trigger a sync with Fivetran",
            href: "/extensions/fivetran",
          },
          {
            label: "Trigger a sync with Airflow",
            href: "/extensions/airflow",
          },
          {
            label: "Trigger a sync with Dagster",
            href: "/extensions/dagster",
          },
          {
            label: "Trigger a sync with Prefect",
            href: "/extensions/prefect",
          },
          {
            label: "Trigger a sync with the API",
            href: "/developer-tools/api-guide",
          },
        ],
      },
      {
        label: "Monitor and troubleshoot",
        nav: [
          {
            label: "Debug a sync run",
            path: "/debugger",
          },
          {
            label: "Sync retries",
            path: "/retries",
          },
          {
            label: "Configure sync alerts",
            path: "/alerting",
          },
          {
            label: "Warehouse Sync Logs",
            path: "/warehouse-sync-logs",
          },
          {
            label: "Sync error codes",
            href: "/developer-tools/error-codes",
          },
        ],
      },
      {
        label: "Advanced sync behavior",
        nav: [
          {
            label: "Change data capture (CDC)",
            path: "/cdc",
          },
          {
            label: "Resync, reset CDC, or clear and fill",
            path: "/resync-reset-clear",
          },
          {
            label: "Use the Lightning Sync Engine",
            path: "/lightning-sync-engine",
          },
        ],
      },
    ],
  },
  {
    label: "Models",
    path: "/models",
    nav: [
      {
        label: "Overview",
        path: "/creating-models",
      },
      {
        label: "Data types and casting",
        path: "/data-types-casting",
      },
      {
        label: "Choose a modeling method",
        nav: [
          {
            label: "SQL editor",
            path: "/sql-editor",
          },
          {
            label: "Table selector",
            path: "/table-selector",
          },
          {
            label: "dbt models and analyses",
            href: "/extensions/dbt-models",
          },
          {
            label: "Looker Looks",
            href: "/extensions/looker-models",
          },
          {
            label: "Sigma workbooks",
            href: "/extensions/sigma",
          },
          {
            label: "Resolve SQL compilation errors",
            path: "/sql-compilation-errors",
          },
        ],
      },
    ],
  },
  {
    label: "Hightouch Events",
    path: "/events",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Plan your implementation",
        path: "/implementation-plan",
      },
      {
        label: "Migrate to Hightouch Events",
        nav: [
          { label: "Overview", path: "/migration-guide/overview" },
          {
            label: "1. Prepare for migration",
            path: "/migration-guide/preparing-for-migration",
          },
          {
            label: "2. Implement Hightouch Events SDKs",
            path: "/migration-guide/implementing-hightouch-events",
          },
          {
            label: "3. Validate your migration",
            path: "/migration-guide/validating-your-migration",
          },
          {
            label: "4. Unify historical and new data",
            path: "/migration-guide/unifying-data",
          },
          {
            label: "5. Update downstream applications and destinations",
            path: "/migration-guide/updating-downstream-applications",
          },
        ],
      },
      {
        label: "Migrate from a specific platform",
        private: true,
        nav: [
          { label: "Segment", path: "/migration-guide/segment", private: true },
          {
            label: "RudderStack",
            path: "/migration-guide/rudderstack",
            private: true,
          },
          {
            label: "mParticle",
            path: "/migration-guide/mparticle",
            private: true,
            nav: [
              {
                label: "Migrate the mParticle Braze kit",
                path: "/migration-guide/mparticle-braze",
              },
            ],
          },
          {
            label: "Amplitude",
            path: "/migration-guide/amplitude",
            private: true,
          },
        ],
      },
      {
        label: "Collect events",
        nav: [
          { label: "Overview", path: "/sources/overview" },
          {
            label: "Browser (JavaScript)",
            path: "/sdks/browser",
          },
          { label: "Node.js", path: "/sdks/nodejs" },
          { label: "Ruby", path: "/sdks/ruby" },
          { label: "Go", path: "/sdks/go" },
          { label: "PHP", path: "/sdks/php" },
          { label: "Java", path: "/sdks/java" },
          { label: "Python", path: "/sdks/python" },
          { label: "iOS", path: "/sdks/ios" },
          { label: "Android", path: "/sdks/android" },
          { label: "React Native", path: "/sdks/react-native" },
          { label: "Flutter", path: "/sdks/flutter" },
          { label: "C# (.NET)", path: "/sdks/csharp" },
          { label: "HTTP API", path: "/sdks/http" },
          // Non-SDK sources
          { label: "Webhook", path: "/sources/webhook" },
          { label: "Kafka", path: "/sources/kafka" },
          { label: "Confluent", href: "/events/sources/kafka" },
          { label: "Redpanda", href: "/events/sources/kafka" },
          { label: "Google Pub/Sub", path: "/sources/google-pubsub" },
          { label: "Amazon SQS", path: "/sources/amazon-sqs" },
          { label: "Amazon Kinesis", path: "/sources/amazon-kinesis" },
          { label: "First-party tracking", path: "/first-party-tracking" },
        ],
      },
      {
        label: "Data contracts",
        nav: [
          { label: "Managing contracts", path: "/contracts/management" },
          { label: "Events CLI", path: "/contracts/cli" },
          { label: "Handling violations", path: "/contracts/violations" },
          {
            label: "Managing via Git Sync",
            href: "/extensions/git-sync#event-contract-schema",
          },
        ],
      },
      {
        label: "Transform events with functions",
        nav: [
          { label: "Functions overview", path: "/functions/overview" },
          { label: "Create a function", path: "/functions/getting-started" },
          { label: "Handling webhooks", path: "/functions/handling-webhooks" },
        ],
      },
      {
        label: "Send events to your warehouse",
        nav: [
          {
            label: "Configure warehouse destination",
            path: "/warehouses/warehouse",
          },
          { label: "Error handling", path: "/warehouses/error-handling" },
        ],
      },
      {
        label: "Stream events to destinations",
        nav: [{ label: "Event streaming", path: "/event-streaming" }],
      },
      {
        label: "Manage identity",
        private: true,
        nav: [
          { label: "How identity works", path: "/identity", private: true },
        ],
      },
      {
        label: "Manage consent",
        nav: [
          { label: "Consent Manager", path: "/consent/consent-manager" },
          { label: "OneTrust", path: "/consent/onetrust" },
        ],
      },
      {
        label: "Monitor and troubleshoot",
        nav: [
          { label: "Monitor events", path: "/monitoring" },
          { label: "Troubleshoot events", path: "/troubleshooting" },
        ],
      },
      {
        label: "Reference",
        nav: [
          { label: "Event types and payload structure", path: "/event-spec" },
          { label: "Warehouse schema", path: "/warehouses/schema" },
        ],
      },
    ],
  },
  {
    label: "Identity Resolution",
    path: "/identity-resolution",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Quick start",
        path: "/get-started",
      },
      {
        label: "How Identity Resolution works",
        nav: [
          {
            label: "Architecture and concepts",
            path: "/how-it-works",
          },
          {
            label: "Deterministic matching",
            path: "/deterministic",
          },
        ],
      },
      {
        label: "Set up your identity graph",
        nav: [
          {
            label: "Prepare your data",
            path: "/prepare-data",
          },
          {
            label: "Create an identity graph",
            path: "/identity-graph",
          },
          {
            label: "Identifier rules",
            path: "/identifier-rules",
          },
          {
            label: "Golden Record",
            path: "/golden-record",
          },
        ],
      },
      {
        label: "Use identity data",
        nav: [
          {
            label: "Lookup table usage",
            path: "/usage",
          },
        ],
      },
      {
        label: "Monitor identity graphs",
        nav: [
          {
            label: "Review match results",
            path: "/monitor",
          },
          {
            label: "Troubleshooting",
            path: "/troubleshooting",
          },
          {
            label: "Supercluster detection",
            path: "/supercluster",
          },
          {
            label: "Profile reprocessing API",
            path: "/profile-reprocessing",
          },
        ],
      },
      {
        label: "Manage permissions",
        nav: [
          {
            label: "Permissions",
            path: "/permissions",
          },
        ],
      },
    ],
  },
  {
    label: "Probabilistic Identity Resolution",
    path: "/probabilistic-idr",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Set up storage",
        nav: [
          {
            label: "AWS",
            path: "/aws",
          },
          {
            label: "Google Cloud Storage",
            path: "/gcs",
          },
          {
            label: "Azure Blob Storage",
            path: "/azure",
          },
        ],
      },
    ],
  },
  {
    label: "Customer Studio",
    path: "/customer-studio",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Plan and configure",
        nav: [
          {
            label: "Plan your data model",
            path: "/data-model",
          },
          {
            label: "Schema",
            path: "/schema",
          },
          {
            label: "Email engagement data",
            path: "/email-engagement-data",
          },
          {
            label: "General models",
            path: "/general-models",
          },
          {
            label: "Sampling",
            path: "/sampling",
          },
          {
            label: "Schema Git Sync",
            path: "/schema-setup",
          },
        ],
      },
      {
        label: "Build audiences",
        nav: [
          {
            label: "Build an audience",
            path: "/usage",
          },
          {
            label: "Agent prompting guide",
            path: "/agents",
          },
          {
            label: "Audience filters",
            path: "/audience-filters",
          },
          {
            label: "Traits",
            path: "/traits",
          },
          {
            label: "Templates",
            path: "/templates",
          },
        ],
      },
      {
        label: "Understand audiences",
        nav: [
          {
            label: "Insights",
            path: "/insights",
          },
          {
            label: "Audience dashboards",
            path: "/audience-overviews",
          },
          {
            label: "Profiles",
            path: "/profiles",
          },
          {
            label: "Audience snapshots",
            path: "/audience-snapshots",
          },
        ],
      },
      {
        label: "Activate",
        nav: [
          {
            label: "Sync audiences",
            path: "/syncs",
          },
          {
            label: "Priority lists",
            path: "/priority-lists",
          },
        ],
      },
      {
        label: "Build and run journeys",
        nav: [
          {
            label: "Overview",
            path: "/journeys",
          },
          {
            label: "Create a journey",
            path: "/journeys/create",
          },
          {
            label: "Journey tiles",
            path: "/journeys/tiles",
          },
          {
            label: "Test and launch",
            path: "/journeys/activate",
          },
          {
            label: "Monitor and troubleshoot",
            path: "/journeys/monitor",
          },
          {
            label: "Assign coupons",
            path: "/coupons",
          },
        ],
      },
      {
        label: "Personalize and test",
        nav: [
          {
            label: "Predictive traits",
            path: "/predictive-traits",
          },
          {
            label: "Catalog recommendations",
            path: "/catalog-recommendations",
          },
          {
            label: "Lookalike audiences",
            path: "/lookalike-audiences",
          },
          {
            label: "Experiments",
            path: "/experiments",
          },
        ],
      },
      {
        label: "Govern",
        nav: [
          {
            label: "Destination rules",
            path: "/destination-rules",
          },
          {
            label: "Subsets",
            path: "/subsets",
          },
          {
            label: "Data masking",
            path: "/data-masking",
          },
          {
            label: "OneTrust Native Snowflake App",
            path: "/onetrust",
          },
        ],
      },
    ],
  },
  {
    label: "Real-time personalization",
    path: "/real-time",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "How it works",
        path: "/architecture",
        private: true,
      },
      {
        label: "Set up real-time audiences",
        path: "/setup",
      },
      {
        label: "Personalization API",
        path: "/personalization-api",
      },
    ],
  },
  {
    label: "AI Decisioning",
    path: "/ai-decisioning",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Set up AI Decisioning",
        nav: [
          {
            label: "Configuration",
            path: "/configuration",
          },
          {
            label: "Prepare data for AID",
            path: "/prepare-data",
          },
          {
            label: "Data handling and privacy",
            path: "/security",
            private: true,
          },
          {
            label: "User feature matrix",
            path: "/user-feature-matrix",
            private: true,
          },
          {
            label: "Group assignment",
            path: "/group-assignment",
            private: true,
          },
          {
            label: "Permissions",
            path: "/permissions",
            private: true,
          },
        ],
      },
      {
        label: "Connect destinations",
        nav: [
          {
            label: "Braze",
            path: "/braze",
          },
          {
            label: "Iterable",
            path: "/iterable",
          },
          {
            label: "Salesforce Marketing Cloud",
            path: "/sfmc",
          },
          {
            label: "Custom destinations",
            path: "/custom-destinations",
          },
        ],
      },
      {
        label: "Build agents and messages",
        nav: [
          {
            label: "Create an agent",
            path: "/agents",
          },
          {
            label: "Messages",
            path: "/messages",
          },
        ],
      },
      {
        label: "Test and launch",
        nav: [
          {
            label: "QA guide",
            path: "/qa-guide",
          },
        ],
      },
      {
        label: "Monitor agents",
        nav: [
          {
            label: "Insights",
            path: "/insights",
          },
          {
            label: "Inspector",
            path: "/inspector",
          },
          {
            label: "Health",
            path: "/agent-health",
          },
          {
            label: "Alerting",
            path: "/agent-alerting",
          },
        ],
      },
      {
        label: "Optimize agents",
        nav: [
          {
            label: "Smart suppression",
            path: "/smart-suppression",
            private: true,
          },
          {
            label: "Tags",
            path: "/tags",
          },
          {
            label: "Content analysis and suggestions",
            path: "/content-analysis",
          },
        ],
      },
    ],
  },
  {
    label: "Agents",
    path: "/agents",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Which agent should I use?",
        path: "/compare-agents",
        dividerAfter: true,
      },
      {
        label: "Chats",
        path: "/chats",
      },
      {
        label: "Reports",
        path: "/reports",
      },
      {
        label: "Custom agents",
        path: "/custom-agents",
      },
      {
        label: "Settings",
        path: "/settings",
      },
    ],
  },
  {
    label: "Context Hub",
    path: "/context-hub",
    private: true,
    dividerAfter: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Define your context",
        nav: [
          {
            label: "Brands",
            path: "/brands",
          },
          {
            label: "Business context",
            path: "/business-context",
          },
          {
            label: "Styles and copy references",
            path: "/creative",
          },
        ],
      },
      {
        label: "Set rules and guardrails",
        nav: [
          {
            label: "Guardrails",
            path: "/guardrails",
          },
        ],
      },
      {
        label: "Connect data and assets",
        nav: [
          {
            label: "Assets",
            path: "/assets",
          },
          {
            label: "Connected data",
            path: "/connections",
          },
          {
            label: "External context sources",
            path: "/external",
          },
        ],
      },
    ],
  },
  {
    label: "Ad Studio",
    path: "/ad-studio",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Prompting guide",
        path: "/prompting-guide",
      },
      {
        label: "Create ads",
        nav: [
          {
            label: "Create ads",
            path: "/ads",
          },
          {
            label: "Refine and collaborate",
            path: "/refine",
          },
        ],
      },
      {
        label: "Launch ads",
        nav: [
          {
            label: "Launch to platforms",
            path: "/launch",
          },
          {
            label: "Hand off to Figma",
            path: "/figma",
            dividerAfter: true,
          },
        ],
      },
      {
        label: "Insights",
        path: "/insights",
      },
      {
        label: "Assets",
        href: "/context-hub/assets",
      },
    ],
  },
  {
    label: "Lifecycle Studio",
    path: "/lifecycle-studio",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Add assets",
        nav: [
          {
            label: "Templates",
            path: "/templates",
          },
          {
            label: "Images",
            path: "/images",
          },
        ],
      },
      {
        label: "Create content",
        nav: [
          {
            label: "Campaigns",
            path: "/campaigns",
          },
          {
            label: "Messages",
            path: "/messages",
          },
          {
            label: "Dynamic content",
            path: "/dynamic-content",
          },
        ],
      },
      {
        label: "Workflow management",
        nav: [
          {
            label: "Asana",
            path: "/asana",
          },
        ],
      },
    ],
  },
  {
    label: "Native Delivery",
    path: "/native-delivery",
    private: true,
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Set up push",
        nav: [
          {
            label: "iOS",
            path: "/push-ios",
          },
          {
            label: "Android",
            path: "/push-android",
          },
        ],
      },
      {
        label: "Build a message",
        nav: [
          {
            label: "Build an email",
            path: "/emails",
          },
          {
            label: "Build an SMS",
            path: "/sms",
          },
          {
            label: "Personalize a message",
            path: "/personalization",
          },
          {
            label: "Preview and test",
            path: "/preview-and-test",
          },
        ],
      },
      {
        label: "Send a message",
        nav: [
          {
            label: "Send a campaign",
            path: "/campaigns",
          },
          {
            label: "Send from a journey",
            path: "/journeys",
          },
          {
            label: "Send with the API",
            path: "/api-triggered-sends",
          },
        ],
      },
      {
        label: "Manage sending",
        nav: [
          {
            label: "Approvals",
            path: "/approvals",
          },
          {
            label: "Subscriptions and consent",
            path: "/subscriptions-and-consent",
          },
          {
            label: "Suppression lists",
            path: "/suppression-lists",
          },
          {
            label: "Reporting",
            path: "/reporting",
          },
          {
            label: "Deliverability",
            path: "/deliverability",
          },
          {
            label: "Migrate from another platform",
            path: "/migration",
          },
        ],
      },
    ],
  },
  {
    label: "Intelligence",
    path: "/campaign-intelligence",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Define metrics",
        nav: [
          {
            label: "Metrics",
            path: "/metrics",
          },
        ],
      },
      {
        label: "Build charts",
        nav: [
          {
            label: "Charts overview",
            path: "/charts",
          },
          {
            label: "Insights",
            path: "/insights",
          },
          {
            label: "Funnels",
            path: "/funnels",
          },
          {
            label: "Experiments",
            path: "/experiments",
          },
        ],
      },
      {
        label: "Create custom dashboards",
        nav: [
          {
            label: "Dashboards",
            path: "/dashboards",
          },
        ],
      },
    ],
  },
  {
    label: "Sources",
    path: "/sources",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Manage warehouse compute",
        path: "/warehouse-compute",
      },
      {
        label: "Query tags",
        path: "/query-tags",
        private: true,
      },
      {
        label: "Sample datasets",
        path: "/sample-data",
        dividerAfter: true,
      },
    ],
  },
  {
    label: "Destinations",
    path: "/destinations",
    nav: [
      {
        label: "Overview",
        dividerAfter: true,
        path: "/overview",
      },
      {
        label: "Databricks Zerobus Ingest",
        path: "/databricks-zerobus-ingest",
      },
    ],
  },
  {
    label: "Match Booster",
    path: "/match-booster",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Quick start",
        path: "/quick-start",
      },
      {
        label: "Set up Match Booster",
        nav: [
          {
            label: "Configure on a model",
            path: "/configure-model",
          },
          {
            label: "Enable in syncs",
            path: "/enable-in-syncs",
          },
        ],
      },
      {
        label: "How Match Booster works",
        nav: [
          {
            label: "Offerings",
            path: "/offerings",
          },
          {
            label: "Matching methods",
            path: "/matching-options",
          },
          {
            label: "Targeting levels",
            path: "/targeting-levels",
          },
        ],
      },
      {
        label: "Common questions",
        path: "/faqs",
      },
    ],
  },
  {
    label: "Extensions",
    path: "/extensions",
    nav: [
      {
        label: "Add a connection",
        path: "/connections",
      },
      {
        label: "Set up alerting",
        nav: [
          {
            label: "Alerting overview",
            href: "/syncs/alerting",
          },
          {
            label: "PagerDuty",
            path: "/pagerduty",
          },
          {
            label: "Datadog",
            path: "/datadog",
          },
        ],
      },
      {
        label: "Connect modeling tools",
        nav: [
          {
            label: "dbt models and analyses",
            path: "/dbt-models",
          },
          {
            label: "dbt Cloud models",
            path: "/dbt-cloud#dbt-cloud-model-sync",
          },
          {
            label: "Looker Looks",
            path: "/looker-models",
          },
          {
            label: "Sigma workbooks",
            path: "/sigma",
          },
        ],
      },
      {
        label: "Schedule syncs with orchestrators",
        nav: [
          {
            label: "dbt Cloud",
            path: "/dbt-cloud",
          },
          {
            label: "Fivetran",
            path: "/fivetran",
          },
          {
            label: "Airflow",
            path: "/airflow",
          },
          {
            label: "Dagster",
            path: "/dagster",
          },
          {
            label: "Prefect",
            path: "/prefect",
          },
          {
            label: "Mage",
            path: "/mage",
          },
        ],
      },
      {
        label: "Manage version control",
        nav: [
          {
            label: "Git version control",
            path: "/git-sync",
          },
          {
            label: "dbt observability layer",
            path: "/dbt-observability",
          },
        ],
      },
    ],
  },
  {
    label: "Workspace management",
    path: "/workspace-management",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },
      {
        label: "Governance best practices",
        path: "/governance",
      },
      {
        label: "Set up workspaces",
        nav: [
          {
            label: "Workspaces",
            path: "/workspaces",
          },
          {
            label: "Environments",
            path: "/environments",
          },
        ],
      },
      {
        label: "Manage access",
        nav: [
          {
            label: "Users and groups",
            path: "/user-management",
          },
          {
            label: "Roles",
            path: "/roles",
          },
          {
            label: "SSO and SCIM",
            path: "/sso",
          },
          {
            label: "Spaces",
            path: "/spaces",
            private: true,
          },
        ],
      },
      {
        label: "Manage changes",
        nav: [
          {
            label: "Approval flows",
            path: "/approval-flows",
          },
          {
            label: "Usage",
            path: "/lineage",
          },
          {
            label: "Audit logs",
            path: "/audit-logs",
          },
        ],
      },
      {
        label: "Organize resources",
        nav: [
          {
            label: "Folders, filters, and labels",
            path: "/folders-filters",
          },
        ],
      },
    ],
  },
  {
    label: "Pricing",
    path: "/pricing",
    nav: [
      {
        label: "Self-serve pricing",
        path: "/ss-pricing",
      },
      {
        label: "Business tier pricing",
        path: "/bt-pricing",
      },
    ],
  },
  {
    label: "Security and compliance",
    path: "/security",
    nav: [
      {
        label: "Overview",
        path: "/overview",
      },

      {
        label: "Storage",
        path: "/storage",
      },

      {
        label: "Regions",
        path: "/regions",
      },

      {
        label: "Networking",
        path: "/networking",
        nav: [
          {
            label: "IP addresses",
            path: "/networking#ip-addresses",
          },
          {
            label: "SSH tunneling",
            path: "/ssh-tunneling",
          },
        ],
      },

      {
        label: "Amazon Web Services",
        nav: [
          {
            label: "Overview",
            path: "/aws",
          },

          {
            label: "AWS PrivateLink",
            path: "/aws/privatelink",
          },
        ],
      },

      {
        label: "Google Cloud",
        nav: [
          {
            label: "Overview",
            path: "/gcp",
          },
          {
            label: "Private Service Connect",
            path: "/gcp/private-service-connect",
          },
        ],
      },

      {
        label: "Microsoft Azure",
        nav: [
          {
            label: "Overview",
            path: "/azure",
          },
          {
            label: "Private Link",
            path: "/azure/private-link",
          },
        ],
      },
    ],
  },
  {
    label: "Developer tools",
    path: "/developer-tools",
    nav: [
      {
        label: "APIs",
        nav: [
          {
            label: "API overview",
            path: "/api-guide",
          },
          {
            label: "API reference",
            href: "/api-reference",
          },
          {
            label: "Personalization API",
            href: "/real-time/personalization-api",
          },
        ],
      },
      {
        label: "CLI",
        nav: [
          {
            label: "CLI overview",
            path: "/cli-guide",
          },
          {
            label: "CLI reference",
            path: "/cli-reference",
          },
        ],
      },
      {
        label: "AI tools",
        nav: [
          {
            label: "Docs for AI tools",
            path: "/agent-resources",
          },
        ],
      },
      {
        label: "Reference",
        nav: [
          {
            label: "Error codes",
            path: "/error-codes",
          },
          {
            label: "Warehouse tables",
            path: "/warehouse-tables",
          },
        ],
      },
    ],
  },
  {
    label: "AI integrations",
    path: "/ai-integrations",
    nav: [
      {
        label: "MCP overview",
        path: "/mcp",
      },
      {
        label: "MCP configuration",
        path: "/mcp-server",
        private: true,
      },
      {
        label: "Snowflake CoWork",
        path: "/snowflake-cowork",
      },
    ],
  },
  {
    label: "Changelog",
    path: "/changelog",
  },
];

/**
 * Grouped config for the sidebar nav. Each group has an optional heading.
 * Groups without a heading render their items directly (no section label).
 *
 * Items with a `nav` array render as expandable accordion folders.
 * Items without `nav` render as always-visible direct links.
 *
 * Structure: Marketing and Data platform as product groups,
 * Integrations (catalog), Manage, Resources.
 */
export const groupedConfig: NavGroup[] = (() => {
  const find = (label: string) => {
    const item = config.find((i) => i.label === label);
    if (!item) {
      throw new Error(`Nav item "${label}" not found in config`);
    }
    return item;
  };

  return [
    {
      heading: "Get started",
      items: [
        {
          label: "What is Hightouch?",
          path: "/getting-started/welcome",
          href: "/",
        },
        {
          label: "Start here",
          path: "/getting-started",
          nav: [
            { label: "Platform admins", path: "/it" },
            { label: "Data teams", path: "/data" },
            { label: "Marketers", path: "/business" },
          ],
        },
      ],
    },
    {
      heading: "Marketing",
      items: [
        find("Customer Studio"),
        find("Agents"),
        find("Context Hub"),
        find("Ad Studio"),
        find("Lifecycle Studio"),
        find("Native Delivery"),
        find("AI Decisioning"),
        find("Intelligence"),
      ],
    },
    {
      heading: "Data platform",
      items: [
        {
          label: "How data activation works",
          path: "/getting-started/concepts",
        },
        find("Models"),
        find("Syncs"),
        find("Hightouch Events"),
        find("Real-time personalization"),
        find("Identity Resolution"),
        find("Probabilistic Identity Resolution"),
        find("Match Booster"),
      ],
    },
    {
      heading: "Integrations",
      items: [
        find("Sources"),
        find("Destinations"),
        find("Extensions"),
        find("AI integrations"),
      ],
    },
    {
      heading: "Manage",
      items: [find("Workspace management"), find("Security and compliance")],
    },
    {
      heading: "Resources",
      items: [find("Pricing"), find("Developer tools"), find("Changelog")],
    },
  ];
})();
